import { useState } from 'react';
import { cn } from '../../lib/utils';
import { CalendarDays, ChevronLeft, ChevronRight, List, LayoutGrid, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { PatientCardList } from './LuminaMADashboard';
import { useAppMode } from '../../contexts/AppModeContext';

const today = new Date();
const buildDate = (offset) => { const d = new Date(); d.setDate(d.getDate() + offset); return d; };
const fmt = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const fmtFull = (d) => d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

const buildPatients = (date, list) => list.map((p, i) => ({
  id: `PREP-${date}-${i}`, name: p.name, initials: p.name.split(' ').map(n => n[0]).join(''),
  time: p.time, type: p.type, chief: p.chief,
  tasks: p.tasks.map(t => ({ key: t.key, label: t.label, done: t.done, type: 'lens', blocked: t.blocked || false, blockReason: t.blockReason || '' })),
  roomReady: p.roomReady || false,
  docs: p.docs || [],
  flagged: p.flagged || false, flagReason: p.flagReason || '',
}));

const PREP_DATA_RAW = {
  0: [
    { name: 'John Smith', time: '9:00 AM', type: 'Follow-up', chief: 'Diabetes management', tasks: [{ key: 'intake', label: 'Intake forms', done: true }, { key: 'vitals', label: 'Vitals', done: true }, { key: 'prior_note', label: 'Prior visit note', done: false }, { key: 'lab_results', label: 'Lab results', done: false }], roomReady: false, docs: [{ id: 'pd1', name: 'Lab Report', type: 'Lab result', time: '8:15 AM', extracted: [{ name: 'Test', value: 'HbA1c', confidence: 96 }] }], flagged: false },
    { name: 'Maria Garcia', time: '9:30 AM', type: 'New Patient', chief: 'Annual physical', tasks: [{ key: 'intake', label: 'Intake forms', done: true }, { key: 'vitals', label: 'Vitals', done: false, blocked: true, blockReason: 'enter now' }, { key: 'new_patient', label: 'New patient forms', done: false }, { key: 'insurance', label: 'Insurance card', done: false }], roomReady: false, docs: [], flagged: true, flagReason: 'vitals not yet entered' },
    { name: 'Robert Johnson', time: '10:00 AM', type: 'Sick Visit', chief: 'Persistent cough', tasks: [{ key: 'intake', label: 'Intake forms', done: false, blocked: true, blockReason: 'missing' }, { key: 'vitals', label: 'Vitals', done: false }, { key: 'prior_note', label: 'Prior visit note', done: false }, { key: 'referral', label: 'Referral doc', done: false }], roomReady: false, docs: [], flagged: true, flagReason: 'intake forms missing' },
    { name: 'Emily Davis', time: '10:30 AM', type: 'Follow-up', chief: 'Cardiology referral', tasks: [{ key: 'intake', label: 'Intake forms', done: true }, { key: 'vitals', label: 'Vitals', done: true }, { key: 'prior_note', label: 'Prior visit note', done: true }, { key: 'clinical_docs', label: 'Clinical docs', done: true }], roomReady: true, docs: [{ id: 'pd2', name: 'Referral Letter', type: 'Referral', time: '7:50 AM', extracted: [{ name: 'From', value: 'Dr. Patel', confidence: 97 }] }], flagged: false },
    { name: 'Sarah Williams', time: '11:00 AM', type: 'Follow-up', chief: 'HTN med check', tasks: [{ key: 'intake', label: 'Intake forms', done: false }, { key: 'vitals', label: 'Vitals', done: false }, { key: 'prior_note', label: 'Prior visit note', done: false }, { key: 'schedule', label: 'Patient schedule', done: false }], roomReady: false, docs: [], flagged: false },
  ],
  1: [
    { name: 'Carlos Mendez', time: '9:00 AM', type: 'Follow-up', chief: 'Cholesterol review', tasks: [{ key: 'intake', label: 'Intake forms', done: false }, { key: 'vitals', label: 'Vitals', done: false }, { key: 'lab_results', label: 'Lab results', done: false }], docs: [] },
    { name: 'Patricia Lee', time: '9:30 AM', type: 'New Patient', chief: 'Wellness check', tasks: [{ key: 'intake', label: 'Intake forms', done: false }, { key: 'vitals', label: 'Vitals', done: false }, { key: 'new_patient', label: 'New patient forms', done: false }, { key: 'insurance', label: 'Insurance card', done: false }], docs: [] },
    { name: 'William Turner', time: '10:00 AM', type: 'Sick Visit', chief: 'Knee pain', tasks: [{ key: 'intake', label: 'Intake forms', done: false }, { key: 'vitals', label: 'Vitals', done: false }, { key: 'referral', label: 'Referral doc', done: false }], docs: [] },
  ],
  2: [
    { name: 'Kevin Robinson', time: '9:00 AM', type: 'Follow-up', chief: 'Post-op check', tasks: [{ key: 'intake', label: 'Intake forms', done: false }, { key: 'vitals', label: 'Vitals', done: false }, { key: 'clinical_docs', label: 'Clinical docs', done: false }], docs: [] },
    { name: 'Amanda Scott', time: '9:30 AM', type: 'Sick Visit', chief: 'Allergy symptoms', tasks: [{ key: 'intake', label: 'Intake forms', done: false }, { key: 'vitals', label: 'Vitals', done: false }], docs: [] },
  ],
};

const DATE_OPTIONS = [
  { label: 'Today', offset: 0 },
  { label: 'Tomorrow', offset: 1 },
  { label: fmt(buildDate(2)), offset: 2 },
  { label: fmt(buildDate(3)), offset: 3 },
  { label: fmt(buildDate(4)), offset: 4 },
];

const statusConfig = {
  'ready': { label: 'Ready', bg: '#f0fdf4', color: '#16a34a', icon: CheckCircle2 },
  'in-progress': { label: 'In progress', bg: '#fffbeb', color: '#92400e', icon: Clock },
  'flagged': { label: 'Flagged', bg: '#fef2f2', color: '#991b1b', icon: AlertTriangle },
  'not-started': { label: 'Not started', bg: '#f5f5f5', color: '#888888', icon: Clock },
};

const getPatientStatus = (p) => {
  const done = p.tasks.filter(t => t.done).length;
  if (done === p.tasks.length && p.roomReady) return 'ready';
  if (p.flagged) return 'flagged';
  if (done > 0) return 'in-progress';
  return 'not-started';
};

const PreVisitPrepPage = () => {
  const { isDemo } = useAppMode();
  const [selectedOffset, setSelectedOffset] = useState(0);
  const [view, setView] = useState('list');
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [expandedCard, setExpandedCard] = useState(null);
  const [cardTab, setCardTab] = useState({});
  const [patientsState, setPatientsState] = useState(() => {
    const state = {};
    Object.keys(PREP_DATA_RAW).forEach(offset => {
      const dateKey = buildDate(parseInt(offset)).toISOString().slice(0, 10);
      state[dateKey] = buildPatients(dateKey, PREP_DATA_RAW[offset]);
    });
    return state;
  });

  const selectedDate = buildDate(selectedOffset);
  const selectedDateKey = selectedDate.toISOString().slice(0, 10);
  const patientsForDate = patientsState[selectedDateKey] || [];

  const setPatients = (updater) => {
    setPatientsState(prev => ({
      ...prev,
      [selectedDateKey]: typeof updater === 'function' ? updater(prev[selectedDateKey] || []) : updater,
    }));
  };

  // Calendar helpers
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDay = new Date(calYear, calMonth, 1).getDay();
  const monthName = new Date(calYear, calMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const calDays = [];
  for (let i = 0; i < firstDay; i++) calDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calDays.push(i);

  const getCalDateKey = (day) => day ? new Date(calYear, calMonth, day).toISOString().slice(0, 10) : null;

  return (
    <div style={{ fontFamily: 'system-ui, Inter, sans-serif' }} data-testid="pre-visit-prep-page">
      {/* Header */}
      <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 500, color: '#111111', margin: 0 }}>Pre-visit prep</h1>
          <p style={{ fontSize: 12, color: '#888888', marginTop: 2 }}>Prepare patients for upcoming visits across all dates</p>
        </div>
        <div className="flex items-center" style={{ gap: 6 }}>
          <button onClick={() => setView('list')}
            style={{ width: 32, height: 32, borderRadius: 6, border: '0.5px solid #e0e0e0', background: view === 'list' ? '#111111' : '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            data-testid="view-list">
            <List style={{ width: 14, height: 14, color: view === 'list' ? '#ffffff' : '#888' }} />
          </button>
          <button onClick={() => setView('calendar')}
            style={{ width: 32, height: 32, borderRadius: 6, border: '0.5px solid #e0e0e0', background: view === 'calendar' ? '#111111' : '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            data-testid="view-calendar">
            <LayoutGrid style={{ width: 14, height: 14, color: view === 'calendar' ? '#ffffff' : '#888' }} />
          </button>
        </div>
      </div>

      {/* Date Filter Bar */}
      <div className="flex items-center" style={{ gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {DATE_OPTIONS.map(opt => {
          const count = (patientsState[buildDate(opt.offset).toISOString().slice(0, 10)] || []).length;
          const isActive = selectedOffset === opt.offset;
          return (
            <button key={opt.offset}
              onClick={() => { setSelectedOffset(opt.offset); setView('list'); }}
              style={{
                height: 32, padding: '0 12px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: 'none',
                background: isActive ? '#111111' : '#ffffff', color: isActive ? '#ffffff' : '#555555',
                boxShadow: isActive ? 'none' : 'inset 0 0 0 0.5px #e0e0e0',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
              data-testid={`date-offset-${opt.offset}`}
            >
              {opt.label}
              {count > 0 && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 10, background: isActive ? 'rgba(255,255,255,0.2)' : '#f0f0f0', color: isActive ? '#ffffff' : '#888' }}>{count}</span>}
            </button>
          );
        })}
        <button onClick={() => setView('calendar')}
          style={{ height: 32, padding: '0 10px', borderRadius: 8, fontSize: 12, color: '#6b5ce7', background: 'none', border: '0.5px solid #e0e0e0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <CalendarDays style={{ width: 12, height: 12 }} /> Calendar
        </button>
      </div>

      {view === 'list' ? (
        <>
          <div style={{ marginBottom: 12 }}>
            <p style={{ fontSize: 14, fontWeight: 500, color: '#111111' }}>{fmtFull(selectedDate)}</p>
            <p style={{ fontSize: 12, color: '#aaaaaa' }}>{patientsForDate.length} patient{patientsForDate.length !== 1 ? 's' : ''}</p>
          </div>

          {patientsForDate.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <CalendarDays style={{ width: 24, height: 24, color: '#cccccc', margin: '0 auto 8px' }} />
              <p style={{ fontSize: 13, color: '#aaaaaa' }}>No patients scheduled for this date</p>
            </div>
          ) : (
            <PatientCardList
              patients={patientsForDate}
              setPatients={setPatients}
              expandedCard={expandedCard}
              setExpandedCard={setExpandedCard}
              cardTab={cardTab}
              setCardTab={setCardTab}
            />
          )}
        </>
      ) : (
        /* Calendar View */
        <div style={{ background: '#ffffff', border: '0.5px solid #e8e8e8', borderRadius: 12, overflow: 'hidden' }} data-testid="calendar-view">
          <div className="flex items-center justify-between" style={{ padding: '14px 16px', borderBottom: '0.5px solid #f0f0f0' }}>
            <button onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); }}
              style={{ width: 28, height: 28, borderRadius: 6, border: '0.5px solid #e0e0e0', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ChevronLeft style={{ width: 14, height: 14, color: '#555' }} />
            </button>
            <span style={{ fontSize: 14, fontWeight: 500, color: '#111111' }}>{monthName}</span>
            <button onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); }}
              style={{ width: 28, height: 28, borderRadius: 6, border: '0.5px solid #e0e0e0', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ChevronRight style={{ width: 14, height: 14, color: '#555' }} />
            </button>
          </div>
          <div className="grid grid-cols-7">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} style={{ padding: '8px 4px', textAlign: 'center', fontSize: 10, color: '#aaaaaa', fontWeight: 500, borderBottom: '0.5px solid #f0f0f0' }}>{d}</div>
            ))}
            {calDays.map((day, i) => {
              const dateKey = getCalDateKey(day);
              const pts = dateKey ? (patientsState[dateKey] || []) : [];
              const isToday = dateKey === today.toISOString().slice(0, 10);
              const isSelected = dateKey === selectedDateKey;
              const flaggedCount = pts.filter(p => p.flagged).length;
              return (
                <div key={i}
                  onClick={() => {
                    if (day) {
                      const diff = Math.round((new Date(calYear, calMonth, day) - today) / (1000 * 60 * 60 * 24));
                      setSelectedOffset(diff);
                      setView('list');
                    }
                  }}
                  style={{
                    padding: '6px 4px', minHeight: 60, borderBottom: '0.5px solid #f5f5f5',
                    borderRight: (i + 1) % 7 !== 0 ? '0.5px solid #f5f5f5' : 'none',
                    cursor: day ? 'pointer' : 'default',
                    background: isSelected ? '#f5f3ff' : isToday ? '#fafafa' : '#ffffff',
                  }}
                >
                  {day && (
                    <>
                      <p style={{ fontSize: 12, fontWeight: isToday ? 500 : 400, color: isToday ? '#6b5ce7' : '#555', margin: '0 0 2px', textAlign: 'right', paddingRight: 4 }}>{day}</p>
                      {pts.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 2px' }}>
                          <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, background: '#f0f0f0', color: '#555', textAlign: 'center' }}>{pts.length} pts</span>
                          {flaggedCount > 0 && (
                            <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, background: '#fef2f2', color: '#991b1b', textAlign: 'center' }}>{flaggedCount} flagged</span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PreVisitPrepPage;
